import { Injectable, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { DuesyApiService, DuesyServerMessage, DuesySource } from './duesy-api.service';
import { DuesyPage, resolveDuesyPage } from './duesy-pages';

export type DuesyPanelState = 'closed' | 'open' | 'minimized';

export interface DuesyMessage {
  localId: string;
  id: string | null;
  role: 'user' | 'assistant';
  content: string;
  sources: DuesySource[];
  unanswered: boolean;
  createdAt: Date;
  status: 'streaming' | 'done' | 'error';
  errorMessage?: string;
  retryable?: boolean;
  /** For an assistant message: the question it answers (used by Retry). */
  question?: string;
  rating?: 'up' | 'down';
}

export interface DuesyState {
  enabled: boolean;
  panel: DuesyPanelState;
  conversationId: string | null;
  messages: DuesyMessage[];
  sending: boolean;
  firstVisit: boolean;
  maxMessageLength: number;
}

const PANEL_KEY = 'duesy.panel';
const SEEN_KEY = 'duesy.seen';

function storageGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function storageSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // storage unavailable (private mode); state just won't persist
  }
}

let nextLocalId = 0;
const localId = () => `duesy-${++nextLocalId}`;

/**
 * Root-level Duesy state. MasterLayoutComponent is re-created for every lazy
 * feature module, so the panel, conversation, and stream live here to
 * survive route changes; the conversation is restored from the server on reload.
 */
@Injectable({ providedIn: 'root' })
export class DuesyStateService implements OnDestroy {
  private readonly stateSubject = new BehaviorSubject<DuesyState>({
    enabled: false,
    panel: 'closed',
    conversationId: null,
    messages: [],
    sending: false,
    firstVisit: storageGet(SEEN_KEY) !== '1',
    maxMessageLength: 1000,
  });
  readonly state$ = this.stateSubject.asObservable();
  readonly page$ = new BehaviorSubject<DuesyPage>(resolveDuesyPage(this.router.url));
  /** Emits the text of each completed answer, for the aria-live announcement. */
  readonly answerCompleted$ = new Subject<string>();

  private chatSub: Subscription | null = null;
  private activeOrgId: string | null = null;
  private readonly destroy$ = new Subject<void>();

  constructor(private api: DuesyApiService, private router: Router) {
    this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd), takeUntil(this.destroy$))
      .subscribe((e) => this.page$.next(resolveDuesyPage((e as NavigationEnd).urlAfterRedirects)));
  }

  get state(): DuesyState {
    return this.stateSubject.value;
  }

  private patch(changes: Partial<DuesyState>): void {
    this.stateSubject.next({ ...this.state, ...changes });
  }

  private updateMessage(id: string, changes: Partial<DuesyMessage>): void {
    this.patch({ messages: this.state.messages.map((m) => (m.localId === id ? { ...m, ...changes } : m)) });
  }

  /** Called when the signed-in organization changes (or on sign-out with null). */
  activate(organizationId: string | null): void {
    if (organizationId === this.activeOrgId) { return; }
    this.activeOrgId = organizationId;
    this.cancelStream();
    this.patch({ enabled: false, conversationId: null, messages: [], sending: false });
    if (!organizationId) { return; }

    this.api.status().subscribe({
      next: (status) => {
        if (organizationId !== this.activeOrgId) { return; }
        this.patch({ enabled: status.enabled, maxMessageLength: status.maxMessageLength || 1000 });
        if (status.enabled) { this.restore(); }
      },
      error: () => this.patch({ enabled: false }),
    });
  }

  private restore(): void {
    const saved = storageGet(PANEL_KEY) as DuesyPanelState | null;
    if (saved === 'open' || saved === 'minimized') { this.patch({ panel: saved }); }
    this.api.currentConversation().subscribe({
      next: ({ conversation, messages }) => {
        if (!conversation || this.state.messages.length) { return; }
        this.patch({ conversationId: conversation.id, messages: messages.map(fromServer) });
      },
      error: () => undefined,
    });
  }

  setPanel(panel: DuesyPanelState): void {
    this.patch({ panel });
    storageSet(PANEL_KEY, panel);
    if (panel === 'open' && this.state.firstVisit) {
      storageSet(SEEN_KEY, '1');
      this.patch({ firstVisit: false });
    }
  }

  newChat(): void {
    this.cancelStream();
    this.patch({ conversationId: null, messages: [], sending: false });
  }

  send(text: string): void {
    const question = text.trim();
    if (!question || this.state.sending) { return; }
    const now = new Date();
    const base = { id: null, sources: [], unanswered: false, createdAt: now };
    const user: DuesyMessage = { ...base, localId: localId(), role: 'user', content: question, status: 'done' };
    const answer: DuesyMessage = { ...base, localId: localId(), role: 'assistant', content: '', status: 'streaming', question };
    this.patch({ messages: [...this.state.messages, user, answer] });
    this.stream(answer.localId, question);
  }

  /** Resends the question behind a failed answer, reusing the same bubble. */
  retry(answerLocalId: string): void {
    const failed = this.state.messages.find((m) => m.localId === answerLocalId);
    if (!failed || failed.status !== 'error' || !failed.question || this.state.sending) { return; }
    this.updateMessage(answerLocalId, {
      status: 'streaming',
      content: '',
      errorMessage: undefined,
      retryable: undefined,
      createdAt: new Date(),
    });
    this.stream(answerLocalId, failed.question);
  }

  rate(message: DuesyMessage, rating: 'up' | 'down'): void {
    if (!message.id) { return; }
    this.updateMessage(message.localId, { rating });
    this.api.feedback(message.id, rating).subscribe({ error: () => this.updateMessage(message.localId, { rating: undefined }) });
  }

  private stream(answerId: string, question: string): void {
    this.patch({ sending: true });
    const pageKey = this.page$.value.pageKey;
    let text = '';
    this.chatSub = this.api.chat({ message: question, pageKey, conversationId: this.state.conversationId }).subscribe({
      next: (event) => {
        if (event.type === 'delta') {
          text += event.text;
          this.updateMessage(answerId, { content: text });
        } else if (event.type === 'done') {
          const d = event.data;
          this.updateMessage(answerId, {
            id: d.messageId,
            content: d.content,
            sources: d.sources || [],
            unanswered: d.unanswered,
            createdAt: new Date(d.createdAt),
            status: 'done',
          });
          this.patch({ conversationId: d.conversationId || this.state.conversationId, sending: false });
          this.answerCompleted$.next(d.content);
        } else {
          this.updateMessage(answerId, { status: 'error', errorMessage: event.message, retryable: event.retryable });
          this.patch({ sending: false });
          this.answerCompleted$.next(event.message);
        }
      },
      complete: () => {
        if (this.state.sending) { this.patch({ sending: false }); }
      },
    });
  }

  private cancelStream(): void {
    if (this.chatSub) {
      this.chatSub.unsubscribe();
      this.chatSub = null;
    }
  }

  ngOnDestroy(): void {
    this.cancelStream();
    this.destroy$.next();
    this.destroy$.complete();
  }
}

function fromServer(m: DuesyServerMessage): DuesyMessage {
  return {
    localId: localId(),
    id: m.id,
    role: m.role,
    content: m.content,
    sources: m.sources || [],
    unanswered: m.unanswered,
    createdAt: new Date(m.createdAt),
    status: 'done',
  };
}
